import { Drawer as MuiDrawer } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const DRAWER_WIDTH = 490

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'hasScrollbar'
})<{ hasScrollbar?: string }>`
  z-index: 10002;

  .MuiPaper-root {
    background: ${theme.colors.legacySurfaceContainerBackground};
    box-shadow: ${theme.attributes.boxShadow.selectFieldPaper};
    width: ${DRAWER_WIDTH}px;
    overflow: hiden;

    /* Scoped here: the field component is shared with the labels drawer. */
    html[data-theme='light'] & {
      background: ${theme.colors.surfaceContainerBackground};

      .header .close-icon svg path {
        fill: ${theme.colors.textCaption};
      }

      .also-available-text {
        color: ${theme.colors.foreground};
      }

      .benchmark-header,
      .form-control-label {
        color: ${theme.colors.textSecondary};
      }

      .MuiOutlinedInput-root .MuiOutlinedInput-input {
        color: ${theme.colors.foreground};
        -webkit-text-fill-color: ${theme.colors.foreground};
      }

      .MuiInputAdornment-root .MuiTypography-root {
        color: #9898af !important;
      }
    }

    .wrapper-inner {
      width: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      height: calc(100% - 152px);
      padding: ${theme.spacing.xLarge}px ${theme.spacing.xLarge}px ${theme.spacing.large}px;

      ${({ hasScrollbar }) =>
        hasScrollbar &&
        hasScrollbar === 'true' &&
        `
        padding-right: 27px;
      `};

      ::-webkit-scrollbar {
        width: 13px !important;
        background: transparent;
      }

      ::-webkit-scrollbar-track {
        box-shadow: none;
        background: transparent;
      }

      ::-webkit-scrollbar-thumb {
        background-color: ${theme.colors.legacy372e35};
        border-radius: ${theme.attributes.borderRadius.quaternary};
      }

      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        margin-bottom: ${theme.spacing.large}px;

        .close-icon {
          width: 40px;
          height: 40px;

          &:hover {
            background: rgba(202, 196, 208, 0.12);
          }

          svg {
            path {
              fill: ${theme.colors.foreground};
            }
          }
        }

        .title {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.xxxxLarge};
          line-height: 42px;
          color: ${theme.colors.menuItemText};
        }
      }

      .cpi-wrapper {
        display: inline-flex;
        border-radius: 18px;
        background: #debdd9;
        box-shadow: ${theme.attributes.boxShadow.quaternary};
        padding: 2px ${theme.spacing.xSmall}px;
        margin-bottom: ${theme.spacing.medium}px;

        .MuiTypography-root {
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 16px;
          letter-spacing: 0.5px;
          color: #3f293e;
        }
      }

      .content {
        display: flex;
        flex-flow: column wrap;
        gap: ${theme.spacing.large}px;

        .tooltip-icon svg path {
          fill: ${theme.colors.caption};
        }

        .select-category-wrapper {
          display: flex;
          align-items: center;
          gap: ${theme.spacing.xSmall}px;
          flex-direction: column;
          margin-bottom: ${theme.spacing.medium}px;

          .select-category-header {
            .MuiTypography-root {
              color: ${theme.colors.foreground};
              font-family: ${theme.typography.fontFamily.primary};
              font-size: ${theme.typography.fontSize.xLarge};
              font-style: normal;
              font-weight: ${theme.typography.fontWeight.bold};
              line-height: 24px;
              letter-spacing: 0.1px;
            }
          }

          .select-category-container {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            gap: ${theme.spacing.md}px;
            width: 100%;

            .select-category-text {
              min-width: 75px !important;
              overflow: hidden;
              color: ${theme.colors.foreground};
              text-overflow: ellipsis;
              text-align: left;
              font-family: ${theme.typography.fontFamily.primary};
              font-size: ${theme.typography.fontSize.xLarge};
              font-style: normal;
              font-weight: ${theme.typography.fontWeight.normal};
              line-height: normal;
            }
          }
        }

        .top-section {
          display: flex;
          flex-flow: column wrap;
          width: 100%;
          gap: ${theme.spacing.md}px;

          .instance-list-wrapper {
            display: flex;
            flex-flow: column wrap;
            gap: ${theme.spacing.xSmall}px;
            max-width: 410px;
          }
        }

        .text {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.normal};
          font-size: ${theme.typography.fontSize.large};
          line-height: 22px;
          color: ${theme.colors.btnSecondaryText};
        }

        .red-required {
          display: inline-block;
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.normal};
          font-size: ${theme.typography.fontSize.large};
          color: #ee4359;
          font-style: normal;
          line-height: 22px;
        }

        .text-sub {
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.normal};
          line-height: 16px;
          letter-spacing: 0.4px;
          color: ${theme.colors.legacyWhite2};
        }

        .also-available-wrapper {
          display: flex;
          align-items: center;

          .also-available-text {
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.bold};
            font-size: ${theme.typography.fontSize.large};
            line-height: 20px;
            color: ${theme.colors.legacyNeutral3};
            font-style: normal;
          }

          .also-available-link {
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.bold};
            font-size: ${theme.typography.fontSize.large};
            font-style: normal;
            color: ${theme.colors.cta};
            cursor: pointer;
            text-decoration: underline;
            text-transform: capitalize;
            line-height: 20px;

            &.v2-link {
              text-decoration: none;
            }
          }
        }

        .benchmarks-title-wrapper {
          display: flex;
          align-items: center;
          gap: ${theme.spacing.xSmall}px;

          .benchmarks-title {
            display: flex;
            align-items: flex-start;
            justify-content: center;
            flex-direction: column;

            &.with-info-icon {
              flex-direction: row;
              align-items: center;
              gap: ${theme.spacing.xSmall}px;
            }

            .benchmark-header {
              color: ${theme.colors.btnSecondaryText};
              font-family: ${theme.typography.fontFamily.primary};
              font-size: ${theme.typography.fontSize.large};
              font-weight: ${theme.typography.fontWeight.normal};
              line-height: 22px;
              letter-spacing: 0.1px;
              font-style: normal;

              span {
                color: #ee4359;
              }
            }

            .benchmark-subheader {
              color: ${theme.colors.legacyWhite2};
              font-family: ${theme.typography.fontFamily.primary};
              font-size: ${theme.typography.fontSize.medium};
              font-weight: ${theme.typography.fontWeight.normal};
              line-height: 16px;
              letter-spacing: 0.4px;
            }
          }

          .benchmarks-title-tooltip-wrapper {
            //margin-top: ${theme.spacing.medium}px;
          }
        }

        .benchmarks-wrapper {
          display: flex;
          align-items: center;
          flex-direction: column;
          width: 100%;
          gap: ${theme.spacing.large}px;

          .benchmarks-header {
            border-radius: ${theme.attributes.borderRadius.quaternary} ${theme.attributes.borderRadius.quaternary} 0 0;
            margin-bottom: ${theme.spacing.xSmall}px;
            padding: ${theme.spacing.md}px ${theme.spacing.small}px;
            gap: ${theme.spacing.medium}px;
            height: 56px;
            align-items: center;

            .head-1 {
              font-family: ${theme.typography.fontFamily.primary};
              font-weight: ${theme.typography.fontWeight.bold};
              font-size: ${theme.typography.fontSize.medium};
              line-height: 16px;
              letter-spacing: 0.5px;
              color: ${theme.colors.foreground};
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 80px;
            }

            .head-2 {
              font-family: ${theme.typography.fontFamily.primary};
              font-weight: ${theme.typography.fontWeight.bold};
              font-size: ${theme.typography.fontSize.medium};
              line-height: 16px;
              letter-spacing: 0.5px;
              color: ${theme.colors.foreground};
            }
          }

          .benchmarks-row {
            width: 100%;
            display: flex;
            align-items: center;

            .info-tooltip {
              padding-top: ${theme.spacing.xSmall}px;
            }

            .MuiFormControl-root {
              .all-line {
                display: flex;
                align-items: center;
              }

              .MuiInputLabel-root {
                font-family: ${theme.typography.fontFamily.primary};
                font-weight: ${theme.typography.fontWeight.normal};
                color: ${theme.colors.foreground};
                font-size: ${theme.typography.fontSize.xLarge};
                line-height: 24px;
                letter-spacing: 0.5px;
              }

              .Mui-focused,
              .MuiFormLabel-filled {
                background: ${theme.colors.legacy141218};
                font-family: ${theme.typography.fontFamily.primary};
                font-size: ${theme.typography.fontSize.medium};
                font-style: normal;
                font-weight: ${theme.typography.fontWeight.normal};
                letter-spacing: 0.4px;
                color: ${theme.colors.foreground};
                padding-left: ${theme.spacing.half}px;
                padding-right: ${theme.spacing.half}px;
              }

              .fixed-w input {
                width: 150px !important;
              }

              .MuiFormHelperText-root {
                font-family: ${theme.typography.fontFamily.primary};
                font-weight: ${theme.typography.fontWeight.bold};
                font-size: ${theme.typography.fontSize.mediumSmall};
                line-height: 16px;
                letter-spacing: 0.5px;
                color: #ffb4ab;
                margin-left: 0;
              }
            }

            .MuiInputAdornment-root {
              .MuiTypography-root {
                font-family: ${theme.typography.fontFamily.primary};
                font-size: ${theme.typography.fontSize.xLarge};
                font-style: normal;
                font-weight: ${theme.typography.fontWeight.normal};
                line-height: 24px;
                letter-spacing: 0.5px;
                color: ${theme.colors.caption};
                padding-left: ${theme.spacing.xsm}px;
              }
            }
          }
        }

        .drawer-actions {
          background: ${theme.colors.surfaceContainerBackground};
          padding: 32px 32px 16px;
          width: ${DRAWER_WIDTH}px;
          gap: ${theme.spacing.xSmall}px;
          display: flex;
          flex-flow: row wrap;
          align-items: center;
          justify-content: center;
          position: fixed;
          bottom: 0;
          right: 0;

          .deactivate-button {
            margin-top: ${theme.spacing.small}px;
          }

          .cancel-button {
            min-width: 196px;
          }

          .save-button {
            min-width: 196px;
          }
        }

        .benchmarks-title {
          .MuiTypography-root {
            color: ${theme.colors.btnSecondaryText};
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.large};
            font-style: normal;
            font-weight: ${theme.typography.fontWeight.normal};
            line-height: 22px;
          }
        }
      }
    }
  }
`
